import React, { type FC, useMemo, useState } from 'react'
import { isPending, isRejected, usePromise } from 'react-sync-promise'
import { FormattedNumber } from 'react-intl'
import { Box, type SxProps, type Theme } from '@mui/material'

import type { ReadonlyDate, ReadonlyRecord } from '../../../../utils'

import { type ConnectwareError, type Metrics as MetricsModel, selectLicenseId, Translation } from '../../../../domain'

import { CircularLoader, Table, type TableProps, ToggleButton, ToggleButtonGroup } from '../../common'
import { ErrorMessage } from '../../ErrorMessage'
import { FormattedDateTime, useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecase } from '../../State'

const dateSort = (t: ReadonlyDate): number => t.getTime()

const wrapperStyle: SxProps<Theme> = { py: 4 }
const toggleWrapperStyle: SxProps<Theme> = { mb: 3 }

type MetricsType = 'hourly' | 'daily'

const commonTableProps: Pick<TableProps<ReadonlyRecord<string, unknown>>, 'pagination' | 'translations' | 'search'> = {
    pagination: { pageSize: 15, pageSizeOptions: [15, 50, 100] as const },
    translations: { noResultsOrEmptyTable: Translation.NO_METRICS },
    search: true,
}

export const Metrics: FC = () => {
    const [metricsType, setMetricsType] = useState<MetricsType>('hourly')
    const translator = useTranslator()
    const systemMetricsUsecase = useAppUsecase('systemMetricsUsecase')

    const licenseID = useAppState(selectLicenseId)

    const metrics = usePromise<MetricsModel, ConnectwareError>(useMemo(() => systemMetricsUsecase.fetchMetrics(), [systemMetricsUsecase]))

    if (isPending(metrics)) {
        return <CircularLoader />
    }

    if (isRejected(metrics)) {
        return <ErrorMessage error={metrics.value} stack extras="section" />
    }

    return (
        <Box sx={wrapperStyle}>
            <Box sx={toggleWrapperStyle}>
                <ToggleButtonGroup
                    data-testid="metrics-toggle"
                    value={metricsType}
                    exclusive
                    /**
                     * When we click the same button again, MUI returns null (deselects it).
                     * We only accept a valid type, ignoring null to keep a selection active.
                     */
                    onChange={(_, newType: MetricsType | null) => newType && setMetricsType(newType)}
                >
                    <ToggleButton value="hourly" aria-label={translator.formatTranslation(Translation.MESSAGE_PER_HOUR)} data-testid="hourly-toggle">
                        {translator.formatTranslation(Translation.MESSAGE_PER_HOUR)}
                    </ToggleButton>
                    <ToggleButton value="daily" aria-label={translator.formatTranslation(Translation.MESSAGE_PER_DAY)} data-testid="daily-toggle">
                        {translator.formatTranslation(Translation.MESSAGE_PER_DAY)}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {metricsType === 'hourly' && (
                <Table
                    data-testid="metrics-per-hour"
                    columns={{
                        timestamp: {
                            label: Translation.TIME,
                            customCellRender: (date) => <FormattedDateTime date={date} format="datetime" />,
                            sort: dateSort,
                        },
                        numberOfConnectedDatapoints: { label: Translation.NUMBER_OF_DATAPOINTS, sort: true },
                        numberOfReceivedMessages: {
                            label: Translation.NUMBER_OF_MESSAGES_PER_HOUR,
                            customCellRender: (value) => <FormattedNumber value={value} style="decimal" />,
                            sort: true,
                        },
                    }}
                    sortOrder={{ name: 'timestamp', direction: 'desc' }}
                    data={metrics.value.hourly}
                    onDownloadCSV={() => systemMetricsUsecase.downloadHourlyMetricCSV(metrics.value.hourly)}
                    {...commonTableProps}
                />
            )}

            {metricsType === 'daily' && (
                <Table
                    data-testid="metrics-per-day"
                    columns={{
                        date: {
                            label: Translation.DATE,
                            customCellRender: (date) => <FormattedDateTime date={date} format="dateOnly" />,
                            sort: dateSort,
                        },
                        numberOfConnectedDatapoints: { label: Translation.NUMBER_OF_DATAPOINTS, sort: true },
                        numberOfReceivedMessages: {
                            label: Translation.NUMBER_OF_MESSAGES_PER_DAY,
                            customCellRender: (value) => <FormattedNumber value={value} style="decimal" />,
                            sort: true,
                        },
                    }}
                    sortOrder={{ name: 'date', direction: 'desc' }}
                    data={metrics.value.daily.map((d) => ({ licenseID, ...d }))}
                    onDownloadCSV={() => systemMetricsUsecase.downloadDailyMetricCSV(metrics.value.daily)}
                    {...commonTableProps}
                />
            )}
        </Box>
    )
}
